var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { UserRoleRoleEnum } from "../types/api";
import { AUTHORITY_TYPE_ROLES } from "./authorityTypes";
/*
 * CZNA-1598 — the roles NK names per authority type, as the administrator picks them.
 *
 * Storage did not change: a row is still (authorityType, CATALOGER | CATALOGING_SUPERVISOR,
 * organizationId). The server reads each row as one of the NK roles below and sends that
 * reading as `nkRole`; the screens show and send only that reading, and the server derives
 * role and organization from it and from the user. A row it cannot read as an NK role
 * (`nkRole` null) — and any type holding more than one row — is a NON-STANDARD assignment,
 * left untouched until the administrator picks an NK role for that type.
 */
/**
 * Mirrors `mod-czna-users/.../domain/entity/enums/NkRole.java`, pinned by
 * `roleModelSync.test.ts`. The order is the one every screen lists them in.
 */
export var NK_ROLES = [
    "CATALOGER",
    "LOCAL_SUPERVISOR",
    "NK_SUPERVISOR",
];
/** The per-type select's value for "leave the non-standard rows as they are". */
export var NON_STANDARD = "NONSTANDARD";
export var isNkRole = function (value) {
    return NK_ROLES.includes(value);
};
/** The stored rows of one authority type. */
export var rowsOfType = function (rows, authorityType) {
    return (rows !== null && rows !== void 0 ? rows : []).filter(function (r) { return r.authorityType === authorityType; });
};
/**
 * How the rows of ONE authority type read: no row, exactly one row the server reads as an NK
 * role, or anything else. Two rows are non-standard even when each reads as an NK role — the
 * server refuses a second row for a type, so they cannot be edited as one role.
 */
export var classifyAssignment = function (rows) {
    if (rows.length === 0)
        return { kind: "none" };
    var only = rows[0];
    if (rows.length === 1 && isNkRole(only.nkRole)) {
        return { kind: "standard", nkRole: only.nkRole, row: only };
    }
    return { kind: "nonStandard", rows: rows };
};
export var assignmentFormValue = function (assignment) {
    switch (assignment.kind) {
        case "standard":
            return assignment.nkRole;
        case "nonStandard":
            return NON_STANDARD;
        default:
            return "";
    }
};
export var nkRoleMessageId = function (nkRole) {
    return "ui-czna-users.nkRole.".concat(nkRole);
};
/** A stored role this screen has no label for reads as the broader one, "Supervizor". */
var storedRole = function (role) {
    return AUTHORITY_TYPE_ROLES.includes(role)
        ? role
        : "CATALOGING_SUPERVISOR";
};
/**
 * "Nestandardní přiřazení (Katalogizátor – celostátně + Supervizor – MZK)": every stored row
 * by its stored role and scope. An organization the caller cannot resolve prints as its id —
 * never as "celostátně", which would understate the row's reach.
 */
export var describeNonStandard = function (intl, rows, organizationName) {
    return intl.formatMessage({ id: "ui-czna-users.nkRole.nonStandard" }, {
        detail: rows
            .map(function (r) {
            var _a;
            return intl.formatMessage({ id: "ui-czna-users.nkRole.nonStandard.row" }, {
                role: intl.formatMessage({
                    id: "ui-czna-users.authorityTypeRole.".concat(storedRole(r.role)),
                }),
                scope: r.organizationId
                    ? ((_a = organizationName(r.organizationId)) !== null && _a !== void 0 ? _a : r.organizationId)
                    : intl.formatMessage({
                        id: "ui-czna-users.nkRole.nonStandard.national",
                    }),
            });
        })
            .join(" + "),
    });
};
/**
 * What a role screen says about ONE authority type's assignment: the NK role's name, or the
 * non-standard text of {@link describeNonStandard}; `undefined` when the type is not held.
 * The user detail and My profile (CZNA-1611) both print it, so they name a role the same way.
 */
export var describeAssignment = function (intl, assignment, organizationName) {
    switch (assignment.kind) {
        case "standard":
            return intl.formatMessage({ id: nkRoleMessageId(assignment.nkRole) });
        case "nonStandard":
            return describeNonStandard(intl, assignment.rows, organizationName);
        default:
            return undefined;
    }
};
/**
 * The distinct NK roles a user holds on standard types, in {@link NK_ROLES} order — what the
 * users list shows next to the global roles.
 */
export var standardNkRoles = function (user) {
    var _a;
    var rows = (_a = user.authorityTypeRoles) !== null && _a !== void 0 ? _a : [];
    var held = new Set();
    new Set(rows.map(function (r) { return r.authorityType; })).forEach(function (type) {
        var assignment = classifyAssignment(rowsOfType(rows, type !== null && type !== void 0 ? type : ""));
        if (assignment.kind === "standard")
            held.add(assignment.nkRole);
    });
    return NK_ROLES.filter(function (role) { return held.has(role); });
};
/**
 * The "Role" filter of the users list: the global roles, then the NK roles. Sent as the
 * `roles` query parameter — a user matches when it holds any of them.
 */
export var USER_LIST_ROLE_FILTERS = __spreadArray([
    UserRoleRoleEnum.AUTHORITY_ADMIN,
    UserRoleRoleEnum.USER_ADMIN,
    UserRoleRoleEnum.VOCABULARY_ADMIN,
    UserRoleRoleEnum.USER
], NK_ROLES, true);
export var isUserListRoleFilter = function (value) {
    return USER_LIST_ROLE_FILTERS.includes(value);
};
export var roleFilterMessageId = function (role) {
    return isNkRole(role) ? nkRoleMessageId(role) : "ui-czna-users.role.".concat(role);
};
